import { FormattedDate } from 'react-intl';
import {Instant, LocalDate, LocalDateTime, ZoneId, ZonedDateTime} from '@js-joda/core';
import { PureComponent } from 'react';

import { DisplayFormatsConsumer } from '@tock/coretypes';

export interface DisplayDateProps {
  dateValue: string | LocalDate | LocalDateTime | Instant | Date | ZonedDateTime;
  showWeekday?: boolean;
  showDay?: boolean;
  showMonth?: boolean;
  showYear?: boolean;
  localeFromProps?: string;
  displayDayAs?: 'numeric' | '2-digit';
  displayMonthAs?: 'numeric' | 'short' | 'long';
  displayWeekdayAs?: 'narrow' | 'short' | 'long';
  displayYearAs?: 'numeric' | '2-digit';
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat

/**
 * Displays a date, without the time, in the business locale.
 */
export class DisplayDate extends PureComponent<DisplayDateProps> {
  static defaultProps: Partial<DisplayDateProps> = {
    showDay: true,
    showMonth: true,
    showYear: true,
    displayDayAs: 'numeric',
    displayYearAs: 'numeric',
    displayMonthAs: 'numeric',
    displayWeekdayAs: 'long',
  };

  static dateTimeFormatTest = /T\d\d:\d\d/;

  getDateObject = (localDate: LocalDate | LocalDateTime): Date => {
    return new Date(Date.UTC(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth()));
  };

  getLocalDateObject = (timeZone: string): Date => {
    const { dateValue } = this.props;

    if (typeof dateValue === 'string') {
      return this.getDateObject(
        DisplayDate.dateTimeFormatTest.test(dateValue)
          ? LocalDateTime.parse(dateValue).toLocalDate()
          : LocalDate.parse(dateValue)
      );
    } else if (dateValue instanceof Date) {
      return new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate()));
    } else if (dateValue instanceof Instant) {
      return this.getDateObject(LocalDate.ofInstant(dateValue, ZoneId.of(timeZone)));
    } else if (dateValue instanceof ZonedDateTime) {
      return this.getDateObject(dateValue.toLocalDate());
    } else {
      return this.getDateObject(dateValue);
    }
  };

  render() {
    const {
      displayDayAs,
      displayMonthAs,
      displayWeekdayAs,
      displayYearAs,
      localeFromProps,
      showDay,
      showMonth,
      showWeekday,
      showYear,
    } = this.props;

    return (
      <DisplayFormatsConsumer>
        {(currency: string, locale: string, timeZone: string) => {
          // I don't think this should actually work but it mirrors what the
          // code used to do and will pass typechecks, so:
          const funkyLocaleOverrideProps = {
            locales: [localeFromProps ?? locale],
          };

          const localDate = this.getLocalDateObject(timeZone);
          const dayProps = showDay ? { day: displayDayAs } : {};
          const monthProps = showMonth ? { month: displayMonthAs } : {};
          const weekdayProps = showWeekday ? { weekday: displayWeekdayAs } : {};
          const yearProps = showYear ? { year: displayYearAs } : {};
          return (
            <FormattedDate
              {...funkyLocaleOverrideProps}
              timeZone="UTC"
              value={localDate}
              {...weekdayProps}
              {...yearProps}
              {...monthProps}
              {...dayProps}
            />
          );
        }}
      </DisplayFormatsConsumer>
    );
  }
}
